import type { EventKind, Audience } from '@/types/database';
export type { EventKind, Audience };

export interface CategoryMeta {
  id: EventKind;
  label: string;
  word: string;        // mot éditorial italique
  short: string;       // intro éditoriale
  color: string;       // token Tailwind (sans rgb)
  colorVar: string;    // CSS var
  pattern: PatternId;
  iconKey: IconKey;
  emoji: string;
}

export type IconKey = 'storefront' | 'confetti' | 'mask' | 'music' | 'family' | 'soccer' | 'fork' | 'wheel' | 'fireworks' | 'tent' | 'fair' | 'sparkle' | 'car';
export type PatternId = 'parasols' | 'confetti' | 'curves' | 'waves' | 'organic' | 'grid' | 'damier' | 'radiant' | 'stars' | 'tent' | 'eventails' | 'dots';

export const CATEGORIES: Record<EventKind, CategoryMeta> = {
  market:     { id: 'market',     label: 'Marché',           word: 'Marchés',           short: 'On chine, on flâne, on goûte.',    color: 'terre',       colorVar: '--terre',       pattern: 'parasols',  iconKey: 'storefront', emoji: '🛒' },
  family:     { id: 'family',     label: 'Famille',          word: 'En famille',        short: 'Pensé pour tous les âges.',        color: 'basilic',     colorVar: '--basilic',     pattern: 'organic',   iconKey: 'family',     emoji: '🎠' },
  cultural:   { id: 'cultural',   label: 'Culture',          word: 'Scènes',            short: 'Théâtre, expositions, débats.',    color: 'prune',       colorVar: '--prune',       pattern: 'curves',    iconKey: 'mask',       emoji: '🎭' },
  music:      { id: 'music',      label: 'Musique',          word: 'Concerts',          short: 'Le rideau se lève.',               color: 'encre-nuit',  colorVar: '--encre-nuit',  pattern: 'waves',     iconKey: 'music',      emoji: '🎵' },
  fair:       { id: 'fair',       label: 'Foire',            word: 'Foires',            short: 'On déballe, on déambule.',         color: 'vieil-or',    colorVar: '--vieil-or',    pattern: 'eventails', iconKey: 'fair',       emoji: '🎡' },
  funfair:    { id: 'funfair',    label: 'Fête foraine',     word: 'Manèges',           short: 'Guirlandes et barbe à papa.',      color: 'cerise',      colorVar: '--cerise',      pattern: 'radiant',   iconKey: 'wheel',      emoji: '🎢' },
  circus:     { id: 'circus',     label: 'Cirque',           word: 'Sous le chapiteau', short: 'Acrobates, jongleurs, rires.',     color: 'velours',     colorVar: '--velours',     pattern: 'tent',      iconKey: 'tent',       emoji: '🎪' },
  food:       { id: 'food',       label: 'Gastronomie',      word: 'Goûts',             short: 'À table, on partage.',             color: 'champagne',   colorVar: '--champagne',   pattern: 'damier',    iconKey: 'fork',       emoji: '🍽️' },
  sport:      { id: 'sport',      label: 'Sport',            word: 'Au stade',          short: 'Tribunes et applaudissements.',    color: 'ink-1',       colorVar: '--ink-1',       pattern: 'grid',      iconKey: 'soccer',     emoji: '⚽' },
  motorsport: { id: 'motorsport', label: 'Sport auto',       word: 'En piste',          short: 'Pilotes, circuits, sensations.',   color: 'cerise',      colorVar: '--cerise',      pattern: 'damier',    iconKey: 'car',        emoji: '🏎️' },
  firework:   { id: 'firework',   label: "Feu d'artifice",   word: 'Ciels',             short: 'On lève les yeux.',               color: 'encre-nuit',  colorVar: '--encre-nuit',  pattern: 'stars',     iconKey: 'fireworks',  emoji: '🎆' },
  other:      { id: 'other',      label: 'Sortie',           word: 'Divers',            short: 'À ne pas manquer.',               color: 'ink-2',       colorVar: '--ink-2',       pattern: 'dots',      iconKey: 'sparkle',    emoji: '✨' },
} as any;
// fête locale = on l'aliase sur 'other' avec un override visuel via word; le schéma DB n'a pas d'enum 'fete_locale'. On gère côté display.

export const CATEGORY_LIST: CategoryMeta[] = [
  CATEGORIES.market, CATEGORIES.family, CATEGORIES.cultural, CATEGORIES.music,
  CATEGORIES.fair, CATEGORIES.funfair, CATEGORIES.circus, CATEGORIES.food,
  CATEGORIES.sport, CATEGORIES.motorsport, CATEGORIES.firework, CATEGORIES.other,
];

export function categoryOf(k: EventKind | null | undefined): CategoryMeta {
  return CATEGORIES[(k ?? 'other') as EventKind] ?? CATEGORIES.other;
}

export const AUDIENCE_LABEL: Record<Audience, string> = {
  all: 'Tout public',
  kids: 'Enfants',
  teens: 'Ados',
  adults: 'Adultes',
  seniors: 'Seniors',
};

// Surtitre temporel pour chips de Découvrir
export const TIME_CHIPS = [
  { id: 'now', label: 'Maintenant' },
  { id: 'tonight', label: 'Ce soir' },
  { id: 'tomorrow', label: 'Demain' },
  { id: 'weekend', label: 'Ce week-end' },
  { id: 'week', label: 'Cette semaine' },
  { id: 'month', label: 'Ce mois' },
  { id: 'year', label: 'Cette année' },
  { id: 'range', label: 'Période' },
] as const;
export type TimeChipId = typeof TIME_CHIPS[number]['id'];

export const DISTANCE_CHIPS = [10, 20, 50, 100, 200] as const;
export type DistanceFilter = (typeof DISTANCE_CHIPS)[number] | 'all';
